(function (root, factory) {
  var create = factory();
  if (typeof module === 'object' && module.exports) module.exports = {createCartGiftService: create};
  else root.KMCreateCartGiftService = create;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  return function createCartGiftService(options) {
    var rules = options.rules;
    var routes = options.routes;
    var storage = options.storage;
    var storageKey = 'kmEssentialSelectedGift';
    var selected = null;
    var selectionAutomatic = false;
    var lastCart = null;
    var lastError = null;
    var queue = Promise.resolve();
    var pendingCount = 0;
    var revision = 0;
    var destroyed = false;
    var changed = false;
    var refreshPromise = null;
    var refreshDirty = false;
    var blockedRestoreKey = null;
    var subscribers = new Set();
    const cartState = options.cartState;
    let refreshVersion = null;
    let checkoutPromise = null;

    try {
      var stored = JSON.parse(storage && storage.getItem(storageKey));
      if (stored && stored.variantId) {
        selected = String(stored.variantId);
        selectionAutomatic = stored.automatic === true;
      }
    } catch (error) {}

    function ensureAlive() {
      if (destroyed) throw new Error('Cart gift service destroyed');
    }

    function notify() {
      if (destroyed) return;
      var state = {cart: lastCart, selectedVariantId: selected, pending: pendingCount > 0,
        error: lastError, cartChanged: changed};
      subscribers.forEach(function (listener) { deliver(listener, state); });
      if (!pendingCount) changed = false;
    }

    function deliver(listener, state) {
      // A view failure must not prevent reconciliation or leave the queue pending.
      try { listener(state); } catch (error) {}
    }

    function saveSelection(id, config, automatic) {
      ensureAlive();
      selected = id == null ? null : String(id);
      selectionAutomatic = Boolean(selected && automatic);
      try {
        if (selected) {
          var match = rules.findGiftVariant(config, selected);
          storage && storage.setItem(storageKey, JSON.stringify({variantId: selected,
            handle: match && match.gift.handle, giftId: match && match.gift.id, automatic: selectionAutomatic}));
        } else if (storage) storage.removeItem(storageKey);
      } catch (error) {}
    }

    async function request(url, body) {
      ensureAlive();
      var init = {credentials: 'same-origin', cache: 'no-store',
        headers: {Accept: 'application/json'}};
      if (body) {
        init.method = 'POST';
        init.headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
      const controller = new AbortController();
      init.signal = controller.signal;
      let timer;
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          const error = new Error('Cart verification timed out. Please retry before checkout.');
          error.code = 'KM_CART_TIMEOUT';
          reject(error);
          controller.abort(error);
        }, options.requestTimeoutMs ?? 12000);
      });
      let response;
      let payload;
      try {
        payload = await Promise.race([timeout, (async () => {
          response = await (cartState ? cartState.fetch(url, init, options.fetchImpl) : options.fetchImpl(url, init));
          try { return await response.json(); } catch (error) {
            throw new Error('Unable to read cart response');
          }
        })()]);
      } finally { clearTimeout(timer); }
      ensureAlive();
      if (!response.ok) {
        var requestError = new Error(payload && (payload.description || payload.message) || 'Cart request failed');
        requestError.status = response.status;
        throw requestError;
      }
      if (body) changed = true;
      return payload;
    }

    async function readCart(fresh = true) {
      var cart = await (cartState ? cartState.read(routes.cart, {fresh}) : request(routes.cart));
      if (!cart || !Array.isArray(cart.items) || !Number.isFinite(Number(cart.total_price))) {
        throw new Error('Invalid cart response');
      }
      lastCart = cart;
      return cart;
    }

    function enqueue(operation) {
      if (destroyed) return Promise.reject(new Error('Cart gift service destroyed'));
      pendingCount += 1;
      lastError = null;
      notify();
      var result = queue.then(function () { ensureAlive(); return operation(); });
      queue = result.catch(function () {});
      return result.then(function (cart) {
        pendingCount -= 1;
        lastError = null;
        notify();
        return cart;
      }, function (error) {
        pendingCount -= 1;
        lastError = error;
        notify();
        throw error;
      });
    }

    function usable(config, id) {
      var match = config.enabled ? rules.findGiftVariant(config, id) : null;
      return match && match.variant.available ? match : null;
    }

    function hasGift(cart, id) {
      return cart.items.some(function (item) {
        return rules.isModernGift(item) && String(item.variant_id || item.id) === id;
      });
    }

    function ensureGiftState(cart, config, id, requireSelected) {
      const match = usable(config, id);
      if (requireSelected && match && cart.items.length
        && rules.getEligibleSubtotal(cart, config) >= match.thresholdCents
        && !hasGift(cart, match.variant.id)) {
        throw new Error('Selected gift was not added');
      }
      if (Object.keys(rules.getGiftUpdates(cart, config, id)).length) {
        throw new Error('Invalid cart gifts remain after reconciliation');
      }
      return cart;
    }

    function restoreKey(cart, config, id) {
      var match = rules.findGiftVariant(config, id);
      return [id, match && match.thresholdCents, rules.getEligibleSubtotal(cart, config),
        cart.items.filter(function (item) { return !rules.isModernGift(item); })
          .map(function (item) { return item.key + ':' + item.quantity; }).join('|')].join(';');
    }

    async function reconcile(cart, config, id) {
      var updates = rules.getGiftUpdates(cart, config, id);
      if (Object.keys(updates).length) {
        await request(routes.update, {updates: updates});
        cart = await readCart();
        ensureGiftState(cart, config, id);
      }
      var match = usable(config, id);
      if (match && cart.items.length && rules.getEligibleSubtotal(cart, config) >= match.thresholdCents
        && !hasGift(cart, match.variant.id) && blockedRestoreKey !== restoreKey(cart, config, id)) {
        await request(routes.add, {id: match.variant.id, quantity: 1, properties: {_km_cart_gift: 'true'}});
        cart = await readCart();
        var postAddUpdates = rules.getGiftUpdates(cart, config, id);
        if (Object.keys(postAddUpdates).length) {
          await request(routes.update, {updates: postAddUpdates});
          cart = await readCart();
          ensureGiftState(cart, config, id);
          blockedRestoreKey = restoreKey(cart, config, id);
        } else {
          ensureGiftState(cart, config, id, true);
          blockedRestoreKey = null;
        }
      }
      lastCart = cart;
      return cart;
    }

    async function sync(fresh = true) {
      var config = rules.normalizeConfig(options.getConfig());
      var cart = await readCart(fresh);
      if (!config.enabled) saveSelection(null, config);
      else {
        if (!cart.items.length) saveSelection(null, config);
        if (selected && !usable(config, selected)) saveSelection(null, config);
        if (!selected) {
          var existing = cart.items.find(function (item) {
            return rules.isModernGift(item) && usable(config, String(item.variant_id || item.id));
          });
          if (existing) saveSelection(String(existing.variant_id || existing.id), config);
        }
        if (!selected && cart.items.length) {
          for (var i = 0; i < config.gifts.length; i += 1) {
            var defaults = config.gifts[i].variants.filter(function (variant) { return variant.available; });
            if (!defaults.length) continue;
            var defaultVariant = defaults.reduce(function (first, variant) {
              return variant.thresholdCents < first.thresholdCents ? variant : first;
            });
            saveSelection(defaultVariant.id, config, true);
            break;
          }
        }
      }
      cart = await reconcile(cart, config, selected);
      // Removing the final orphan gift can make a previously nonempty cart empty.
      if (!cart.items.length) saveSelection(null, config);
      return cart;
    }

    function refresh({fresh = true} = {}) {
      if (destroyed) return Promise.reject(new Error('Cart gift service destroyed'));
      if (refreshPromise) {
        if (cartState && refreshVersion !== cartState.version) refreshDirty = true;
        return refreshPromise;
      }
      refreshPromise = enqueue(async function () {
        var cart;
        do {
          refreshDirty = false;
          refreshVersion = cartState?.version;
          cart = await sync(fresh);
          refreshVersion = cartState?.version;
        } while (refreshDirty && !destroyed);
        return cart;
      });
      refreshPromise.then(function () { refreshPromise = null; }, function () { refreshPromise = null; });
      return refreshPromise;
    }

    function chooseGift(variantId) {
      var choiceRevision = ++revision;
      var id = String(variantId);
      return enqueue(async function () {
        var config = rules.normalizeConfig(options.getConfig());
        var match = usable(config, id);
        if (!match) throw new Error(config.messages.unavailable || 'Gift unavailable');
        var cart = await readCart();
        if (choiceRevision !== revision) return cart;
        if (!cart.items.length) {
          saveSelection(null, config);
          throw new Error('Add an item before choosing a gift');
        }
        blockedRestoreKey = null;
        var previous = selected;
        try {
          cart = await reconcile(cart, config, id);
          if (choiceRevision === revision) saveSelection(cart.items.length ? id : null, config);
          return cart;
        } catch (error) {
          // Replacement is two API calls; restore an eligible previous gift only once.
          try {
            cart = await readCart();
            var oldMatch = usable(config, previous);
            if (choiceRevision === revision && oldMatch
              && rules.getEligibleSubtotal(cart, config) >= oldMatch.thresholdCents) {
              await reconcile(cart, config, previous);
            }
          } catch (restoreError) {
            try { await readCart(); } catch (readError) {}
          }
          throw error;
        }
      });
    }

    return {
      refresh: refresh,
      chooseGift: chooseGift,
      prepareCheckout() {
        if (!checkoutPromise) {
          checkoutPromise = enqueue(sync);
          checkoutPromise.then(() => { checkoutPromise = null; }, () => { checkoutPromise = null; });
        }
        return checkoutPromise;
      },
      getSelectedVariantId: function () { return selected; },
      hasExplicitSelection: function () { return Boolean(selected && !selectionAutomatic); },
      subscribe: function (listener) {
        if (destroyed) return function () {};
        subscribers.add(listener);
        deliver(listener, {cart: lastCart, selectedVariantId: selected, pending: pendingCount > 0,
          error: lastError, cartChanged: false});
        return function () { subscribers.delete(listener); };
      },
      destroy: function () { destroyed = true; subscribers.clear(); }
    };
  };
});
